import React, { useState } from 'react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import {
    XCircle,
    CheckCircle,
    Send,
    Mail,
    FileCheck,
} from 'lucide-react';
import { apiClient, Driver } from '../../services/api';

interface ConsentDetailsModalProps {
    driver: Driver;
    onClose: () => void;
    onRefresh: () => void;
}

/**
 * Consent Details Modal
 * Shows consent history, status, and allows withdrawal for a specific driver
 */
const ConsentDetailsModal: React.FC<ConsentDetailsModalProps> = ({
    driver,
    onClose,
    onRefresh
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isSendingConsent, setIsSendingConsent] = useState(false);
    const [isSendingReminder, setIsSendingReminder] = useState(false);
    const [showRecordConsentModal, setShowRecordConsentModal] = useState(false);
    const [consentReference, setConsentReference] = useState('');
    const [isRecordingConsent, setIsRecordingConsent] = useState(false);

    const handleWithdrawConsent = async () => {
        const reason = prompt('Please provide a reason for withdrawing consent:');
        if (!reason) return;

        try {
            setIsLoading(true);
            await apiClient.adminWithdrawConsent(driver.id, reason);
            toast.success('Consent withdrawn successfully');
            onRefresh();
            onClose(); // Close the modal after successful withdrawal
        } catch (err: any) {
            toast.error('Failed to withdraw consent: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSendDigitalConsent = async () => {
        try {
            setIsSendingConsent(true);
            await apiClient.sendConsentRequest({ driverId: driver.id });
            toast.success('Consent request sent successfully');
            onRefresh();
            onClose(); // Close modal after successful send
        } catch (err: any) {
            toast.error('Failed to send consent request: ' + err.message);
        } finally {
            setIsSendingConsent(false);
        }
    };

    const handleSendReminder = async () => {
        try {
            setIsSendingReminder(true);
            await apiClient.sendConsentReminder(driver.id);
            toast.success('Reminder sent successfully');
            onRefresh();
            onClose(); // Close modal after successful send
        } catch (err: any) {
            toast.error('Failed to send reminder: ' + err.message);
        } finally {
            setIsSendingReminder(false);
        }
    };

    const handleRecordOfflineConsent = () => {
        setConsentReference('');
        setShowRecordConsentModal(true);
    };

    const handleSubmitOfflineConsent = async () => {
        if (!consentReference.trim()) return;

        try {
            setIsRecordingConsent(true);

            await apiClient.recordDriverConsent(driver.id, {
                consentReference: consentReference.trim(),
                consentDate: new Date().toISOString(),
                consentMethod: 'offline'
            });

            toast.success('Offline consent recorded successfully!');
            onRefresh();

            // Close both modals
            setShowRecordConsentModal(false);
            setConsentReference('');
            onClose();

        } catch (error: any) {
            toast.error('Failed to record consent: ' + error.message);
        } finally {
            setIsRecordingConsent(false);
        }
    };

    // Check if consent has expired based on expiry date
    const isConsentExpired = driver.consentExpiry && new Date(driver.consentExpiry) < new Date();

    // Check if there's a pending consent request (sent or opened)
    // Note: 'expired' status means invitation expired, not consent itself
    const hasPendingRequest = driver.consentStatus?.tokens?.some(
        (token: any) => token.status === 'sent' || token.status === 'opened'
    );

    // Show consent action buttons if:
    // - Driver doesn't have active consent OR consent has expired based on date
    // - AND no pending invitation (not 'sent' or 'opened')
    // This covers:
    // 1. No consent at all
    // 2. Consent invitation expired (token.status = 'expired')
    // 3. Actual consent expired (consentExpiry date passed)
    // 4. Consent declined (token.status = 'declined')
    const canSendNewConsent = (!driver.consentProvided || isConsentExpired) && !hasPendingRequest;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-gray-900">
                            Consent Details - {driver.firstName} {driver.lastName}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <XCircle className="w-6 h-6" />
                        </button>
                    </div>

                    {/* Driver Info */}
                    <div className="bg-gray-50 rounded-md p-4 mb-6">
                        <h3 className="font-medium text-gray-900 mb-2">Driver Information</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-gray-600">Licence:</span>
                                <span className="ml-2 font-mono">{driver.licenceNumber}</span>
                            </div>
                            <div>
                                <span className="text-gray-600">Email:</span>
                                <span className="ml-2">{driver.email}</span>
                            </div>
                        </div>
                    </div>

                    {/* Current Consent Status */}
                    {driver.consentProvided && !isConsentExpired && (
                        <div className="rounded-md p-4 mb-6" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                            <h3 className="font-medium mb-2 flex items-center" style={{ color: '#231f20' }}>
                                <CheckCircle className="w-4 h-4 mr-2" style={{ color: '#69BE28' }} />
                                Active Consent
                            </h3>
                            <div className="text-sm space-y-1">
                                <div>
                                    <span style={{ color: '#69BE28' }}>Provided:</span>
                                    <span className="ml-2">
                                        {driver.consentDate ? format(new Date(driver.consentDate), 'dd/MM/yyyy HH:mm') : 'Unknown'}
                                    </span>
                                </div>
                                <div>
                                    <span style={{ color: '#69BE28' }}>Valid until:</span>
                                    <span className="ml-2">
                                        {driver.consentExpiry ? format(new Date(driver.consentExpiry), 'dd/MM/yyyy') : 'Unknown'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Expired Consent Status */}
                    {driver.consentProvided && isConsentExpired && (
                        <div className="rounded-md p-4 mb-6 bg-red-50 border border-red-200">
                            <h3 className="font-medium mb-2 flex items-center text-red-800">
                                <XCircle className="w-4 h-4 mr-2" />
                                Consent Expired
                            </h3>
                            <div className="text-sm space-y-1 text-red-700">
                                <div>
                                    <span className="font-medium">Provided:</span>
                                    <span className="ml-2">
                                        {driver.consentDate ? format(new Date(driver.consentDate), 'dd/MM/yyyy HH:mm') : 'Unknown'}
                                    </span>
                                </div>
                                <div>
                                    <span className="font-medium">Expired on:</span>
                                    <span className="ml-2">
                                        {driver.consentExpiry ? format(new Date(driver.consentExpiry), 'dd/MM/yyyy') : 'Unknown'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Token History */}
                    {driver.consentStatus?.tokens && driver.consentStatus.tokens.length > 0 && (
                        <div className="mb-6">
                            <h3 className="font-medium text-gray-900 mb-3">Request History</h3>
                            <div className="space-y-2">
                                {driver.consentStatus.tokens.map((token) => (
                                    <div key={token.id} className="border rounded-md p-3">
                                        <div className="flex items-center justify-between">
                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                                token.status === 'active' ? 'bg-green-100 text-green-800' :
                                                token.status === 'sent' ? '' :
                                                token.status === 'opened' ? 'bg-purple-100 text-purple-800' :
                                                token.status === 'expired' ? 'bg-red-100 text-red-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}
                                            style={token.status === 'sent' ? {
                                                backgroundColor: 'rgba(105, 190, 40, 0.1)',
                                                color: '#69BE28'
                                            } : undefined}>
                                                {token.status.charAt(0).toUpperCase() + token.status.slice(1)}
                                            </span>
                                            <span className="text-xs text-gray-500">
                                                {format(new Date(token.createdAt), 'dd/MM/yyyy HH:mm')}
                                            </span>
                                        </div>
                                        {token.usedAt && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                Completed: {format(new Date(token.usedAt), 'dd/MM/yyyy HH:mm')}
                                            </div>
                                        )}
                                        {token.trackingEvents && token.trackingEvents.filter((e: any) =>
                                            e.eventType === 'sent' && e.metadata?.eventSubtype === 'reminder'
                                        ).length > 0 && (
                                            <div className="mt-2 pt-2 border-t border-gray-200">
                                                <div className="text-xs font-medium text-gray-700 mb-1">Reminders Sent:</div>
                                                <div className="space-y-1">
                                                    {token.trackingEvents
                                                        .filter((e: any) => e.eventType === 'sent' && e.metadata?.eventSubtype === 'reminder')
                                                        .map((event: any, eventIndex: number) => (
                                                            <div key={eventIndex} className="text-xs text-gray-600 ml-2">
                                                                • {format(new Date(event.timestamp), 'dd/MM/yyyy HH:mm')}
                                                                {event.metadata?.reminderCount && ` (Reminder ${event.metadata.reminderCount})`}
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* No consent history */}
                    {(!driver.consentStatus?.tokens || driver.consentStatus.tokens.length === 0) && !driver.consentProvided && (
                        <div className="mb-6 text-center py-8">
                            <p className="text-gray-500">No consent requests sent yet</p>
                        </div>
                    )}

                    {/* Consent Action Buttons */}
                    {(!driver.consentProvided || isConsentExpired) && (
                        <div className="mb-6 space-y-3">
                            <h3 className="font-medium text-gray-900 mb-3">Consent Actions</h3>

                            {isConsentExpired && (
                                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-3">
                                    <p className="text-sm text-yellow-800 font-medium">
                                        ⚠️ Consent has expired. A new consent request is required.
                                    </p>
                                </div>
                            )}

                            {canSendNewConsent ? (
                                <div className="space-y-3">
                                    {/* Send Digital Consent Form - Blue Button */}
                                    <button
                                        onClick={handleSendDigitalConsent}
                                        disabled={isSendingConsent}
                                        className="w-full px-4 py-3 text-white rounded-md font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center"
                                        style={{ backgroundColor: '#4F46E5' }}
                                    >
                                        {isSendingConsent ? (
                                            <>
                                                <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <Send className="w-4 h-4 mr-2" />
                                                Send Digital Consent Form
                                            </>
                                        )}
                                    </button>

                                    {/* Record Offline Consent - Gray Button */}
                                    <button
                                        onClick={handleRecordOfflineConsent}
                                        className="w-full px-4 py-3 bg-gray-100 text-gray-700 rounded-md font-medium hover:bg-gray-200 transition-colors flex items-center justify-center"
                                    >
                                        <FileCheck className="w-4 h-4 mr-2" />
                                        Record Offline Consent
                                    </button>

                                    <p className="text-xs text-gray-500 text-center">
                                        Use "Record Offline Consent" only if driver signed physical D906 form
                                    </p>
                                </div>
                            ) : hasPendingRequest && (
                                /* Send Reminder Button */
                                <button
                                    onClick={handleSendReminder}
                                    disabled={isSendingReminder}
                                    className="w-full px-4 py-3 text-white rounded-md font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center"
                                    style={{ backgroundColor: '#69BE28' }}
                                >
                                    {isSendingReminder ? (
                                        <>
                                            <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <Mail className="w-4 h-4 mr-2" />
                                            Send Reminder
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-between pt-4 border-t">
                        {driver.consentProvided && !isConsentExpired && (
                            <button
                                onClick={handleWithdrawConsent}
                                disabled={isLoading}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                                {isLoading ? 'Processing...' : 'Withdraw Consent'}
                            </button>
                        )}

                        <div className="flex gap-2 ml-auto">
                            <button
                                onClick={onClose}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Record Offline Consent Modal */}
            {showRecordConsentModal && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-[60]">
                    <div className="bg-white rounded-lg max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Record Offline Consent</h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Enter the D906 form reference number for {driver.firstName} {driver.lastName}
                        </p>

                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                D906 Reference Number *
                            </label>
                            <input
                                type="text"
                                value={consentReference}
                                onChange={(e) => setConsentReference(e.target.value)}
                                placeholder="e.g., D906-2024-001234"
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-opacity-50"
                                style={{ outlineColor: '#69BE28' }}
                                autoFocus
                            />
                        </div>

                        <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
                            <p className="text-xs text-blue-800">
                                <strong>Consent Date:</strong> {new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
                            </p>
                            <p className="text-xs text-blue-800 mt-1">
                                <strong>Method:</strong> Offline (Physical D906 Form)
                            </p>
                        </div>

                        <div className="flex space-x-3">
                            <button
                                onClick={() => {
                                    setShowRecordConsentModal(false);
                                    setConsentReference('');
                                }}
                                disabled={isRecordingConsent}
                                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmitOfflineConsent}
                                disabled={isRecordingConsent || !consentReference.trim()}
                                className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                {isRecordingConsent ? (
                                    <div className="flex items-center justify-center">
                                        <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                                        Recording...
                                    </div>
                                ) : (
                                    'Record Consent'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ConsentDetailsModal;
